import { FC } from 'react';
import { useTranslation } from 'next-i18next';
import { Modal } from 'antd';
import cn from 'classnames';
import styled from 'styled-components';

import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useBreakpoints } from '@/shared/hooks/useBreakpoints';
import { Button } from '@/shared/uikitV2/Button';
import { useModalsStore } from '@/widgets/modals/modals.store';

import { ConfirmModalProps } from '../../types';

export const ConfirmModal: FC<ConfirmModalProps> = ({
  onConfirm,
  onCancel,
  title,
  bodyText,
  cancelText,
  confirmText,
  variant = 'delete',
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'actions' });
  const modals = useModalsStore();
  const { lg, sm } = useBreakpoints();

  const modalTitle = title || t('deleteTitle');
  const modalBody = bodyText || t('deleteText');
  const cancelLabel = cancelText || t('cancel');
  const confirmLabel = confirmText || t(variant === 'delete' ? 'delete' : 'confirm');

  const handleSubmit = () => {
    onConfirm();
    modals.closeModal();
  };

  const handleCancel = () => {
    onCancel?.();
    modals.closeModal();
  };

  return (
    <ModalContainer
      open={true}
      centered={lg}
      footer={null}
      width={600}
      title={<Title>{modalTitle}</Title>}
      onCancel={modals.closeModal}
    >
      <Body>
        <Text>{modalBody}</Text>
      </Body>
      <Footer className={cn(sm && 'mobile')}>
        <ButtonCancel onClick={handleCancel} type="default">
          {cancelLabel}
        </ButtonCancel>
        <ActionButton
          className={cn(variant === 'delete' ? 'delete' : 'confirm')}
          onClick={handleSubmit}
        >
          {confirmLabel}
        </ActionButton>
      </Footer>
    </ModalContainer>
  );
};

const ModalContainer = styled(Modal)`
  &&& {
    max-width: 400px;

    .ant-modal-header {
      margin-bottom: 0;
      font-size: 22px;
    }

    .ant-modal-content {
      padding: 16px 0;
    }

    .ant-modal-title {
      padding: 0 0 16px 24px;
      border-bottom: 1px solid ${COLORS.systemDark100};
    }
  }
`;

const Title = styled.span`
  font-weight: 700;
  font-size: 22px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 24px;
`;

const Text = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${COLORS.systemDark200};
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-evenly;
  gap: 16px;
  border-top: 1px solid ${COLORS.systemDark100};
  padding: 16px 24px 0 24px;

  ${media.sm} {
    flex-direction: column-reverse;
  }
`;

const ButtonCancel = styled(Button)`
  && {
    font-weight: bold;
    font-size: 14px;
    width: auto;
    flex-grow: 1;
  }
`;

const ActionButton = styled(ButtonCancel)`
  &.delete {
    background: ${COLORS.systemFlame200};

    &:hover {
      background: ${COLORS.systemFlame300};
    }
  }

  &.confirm {
    background: ${COLORS.systemSky200};

    &:hover {
      background: ${COLORS.systemSky300};
    }
  }
`;
